import { combineReducers } from 'redux';

import carriersReducer from './carriers';
import * as fromCarriers from './carriers/selectors';
import { createItinerariesReducer } from './itineraries';
import * as fromItineraries from './itineraries/selectors';
import legsReducer from './legs';
import * as fromLegs from './legs/selectors';
import segmentsReducer from './segments';
import * as fromSegments from './segments/selectors';

import type { GetExitingItineraryId } from './itineraries';
import type { CarrierBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/flights/carriersBookingPanel';
import type {
  ErrorType,
  FilterStatusMapBookingPanel,
  ItineraryBookingPanel,
  ItineraryByIdBookingPanel,
} from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/flights/itinerariesBookingPanel';
import type { LegBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/flights/legsBookingPanel';
import type { SegmentBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/flights/segmentsBookingPanel';
import type { FlightState } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/flightBookingPanel';

export type { GetExitingItineraryId } from './itineraries';

export const createFlightsReducer = (
  isTokenPanel: () => boolean,
  getExitingId: GetExitingItineraryId,
) => {
  const itinerariesReducer = createItinerariesReducer(isTokenPanel, getExitingId);
  return combineReducers({
    carriers: carriersReducer,
    itineraries: itinerariesReducer,
    legs: legsReducer,
    segments: segmentsReducer,
  });
};

// carriers
export const carrier = (state: FlightState, id: number): CarrierBookingPanel =>
  fromCarriers.get(state.carriers, id);
export const allCarriers = (state: FlightState): Record<number, CarrierBookingPanel> =>
  fromCarriers.getAll(state.carriers);

// legs
export const leg = (state: FlightState, id: string): LegBookingPanel =>
  fromLegs.get(state.legs, id);
export const allLegs = (state: FlightState): Record<string, LegBookingPanel> =>
  fromLegs.all(state.legs);

// segments
export const segment = (state: FlightState, id: string): SegmentBookingPanel =>
  fromSegments.get(state.segments, id);
export const allSegments = (state: FlightState): Record<string, SegmentBookingPanel> =>
  fromSegments.all(state.segments);

// itineraries
export const itinerary = (state: FlightState, id: string): ItineraryBookingPanel =>
  fromItineraries.get(state.itineraries, id);
export const allItineraries = (state: FlightState): ItineraryByIdBookingPanel =>
  fromItineraries.all(state.itineraries);
export const getItineraryFilterStatusMap = (
  state: FlightState,
  id: string,
): FilterStatusMapBookingPanel[] => fromItineraries.getFilterStatusMap(state.itineraries, id);
export const getItineraryError = (state: FlightState, id: string): ErrorType | undefined =>
  fromItineraries.getItineraryError(state.itineraries, id);
